import React, { useState, useEffect, useRef }  from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import QueuedPostRequest from 'components/layout_and_quote_builder/utils/QueuedPostRequest.js';
import { getFromCache, setInCache, hasInCache } from 'components/layout_and_quote_builder/utils/SolutionScoreCache.js';
import { materialSheetLabel } from 'components/layout_and_quote_builder/utils/materialSheetLabel.js';
// import LayoutNavSectionCombinationImg from 'components/layout_and_quote_builder/LayoutNavSectionCombinationImg.jsx'
import LayoutSvg from 'components/layout_and_quote_builder/quote_builder/LayoutSvg.jsx'
import SolutionJsonViewer from 'components/layout_and_quote_builder/SolutionJsonViewer.jsx'
import HoverTooltip from 'components/common/HoverTooltip.jsx';
// Same machine colors + icons as the ManufacturingLine cards (printing → print, die-cutting → scissors).
import { KIND as MACHINE_KIND, MachineIcon } from 'components/layout_and_quote_builder/MachineSheetRectangle.jsx'

// Admin-only debug readout shown under each solution card (window.isAdminShowDebug). Full card width,
// each value on its own row with the label on the left and the value right-aligned in a tabular
// column, so the scores are easy to scan down instead of running together on "label: a | label: b" lines.
const debugPanelStyle = {
  width: '100%',
  boxSizing: 'border-box',
  marginTop: '10px',
  padding: '8px 10px',
  fontSize: '12px',
  lineHeight: 1.5,
  color: '#374151',
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  columnGap: '12px',
  rowGap: '3px',
  alignItems: 'baseline',
};
// The fill of a machine bed, one per machine kind: the ManufacturingLine cards' own colors (MACHINE_KIND)
// at the fainter opacity this figure draws them at, so the icon inside the bed stays readable. Combined
// (purple) is the single bed a one-pass flexo machine draws — see the columns in #card.
const MACHINE_BED_TINT = {
  printing: 'rgba(21, 101, 192, 0.06)',
  die_cutting: 'rgba(46, 125, 50, 0.06)',
  combined: 'rgba(106, 27, 154, 0.06)'
};
const debugLabelStyle = { color: '#6b7280', fontWeight: 400, whiteSpace: 'nowrap' };
// A breakdown row under its parent (e.g. the die-cutting machine / metal-strip parts under "Kesim"):
// indented and lighter so it reads as a detail of the row above.
const debugSubLabelStyle = { color: '#9ca3af', fontWeight: 400, whiteSpace: 'nowrap', paddingLeft: '14px' };
const debugValueStyle = { textAlign: 'right', fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums' };
const debugSeparatorStyle = { gridColumn: '1 / -1', height: '1px', background: '#eef2f7', margin: '3px 0' };

// Group the thousands in score values (84000 → 84.000) so large numbers are readable; leave
// anything non-numeric untouched.
const formatDebugNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return value;
  }
  return number.toLocaleString('tr-TR');
};

const LayoutNavSectionCombination = ( { diecutting_result, nav_section_id, count, layout_dimension, setTotalScoreList, index , setNumTotals, setNumTotalsPrinting, setNumOfDrawingsInSet} ) => {
  const name = `${diecutting_result.uuid}`;
  // Round to 2 decimals and drop trailing zeros (700 -> "700", 499.7999999999995 -> "499.8").
  const formatDimension = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return parseFloat(num.toFixed(2)).toString();
  };

  const [cuttingScore, setCuttingScore] = useState( 0 )
  // The parts of the die-cutting ("Kesim") price: running the die-cutting machine, the metal strips, the cutting
  // board (kesim tahtası) and the female sorting board (Ayıklama tahtası, dişi -- priced only for an automatic
  // machine). null until scored, and left null for an unpriced machine, so the breakdown rows stay hidden then.
  const [cuttingMachineScore, setCuttingMachineScore] = useState( null )
  const [cuttingMetalStripScore, setCuttingMetalStripScore] = useState( null )
  const [cuttingBoardScore, setCuttingBoardScore] = useState( null )
  const [cuttingBoardFemaleScore, setCuttingBoardFemaleScore] = useState( null )
  const [printingScore, setPrintingScore] = useState( 0 )
  // ONE MACHINE, ONE PASS. A combined (flexo) manufacturing line prints AND die-cuts in a single pass, so it is
  // billed once (Frank::FlexoPrintingAndDieCuttingScore) and has no separate Baskı / Kesim figures at all. null
  // for every other solution, which is what switches the panel between the two shapes below -- without it the
  // two-machine rows rendered EMPTY for such a card and the one figure that explains its price was shown nowhere.
  const [flexoPrintingAndDieCuttingScore, setFlexoPrintingAndDieCuttingScore] = useState( null )
  const [materialScore, setMaterialScore] = useState( 0 )
  // The guillotine (cutting-to-size) half. null when the payload carries none -- a legacy score, or a pass that
  // was never guillotine-scored -- which hides the debug row rather than showing a misleading 0.
  const [guillotineScore, setGuillotineScore] = useState( null )
  const [kesim_num_total, set_kesim_num_total] = useState( 0 )
  const [print_num_total, set_print_num_total] = useState( 0 )

  const [score, setScore] = useState( 0 )
  const [printingImageUrl, setPrintingImageUrl] = useState( '')
  const [diecuttingImageUrl, setDiecuttingImageUrl] = useState( '')
  const [numTotal, setNumTotal] = useState(0);
  const [numOfDrawingInSet, setNumOfDrawingInSet] = useState(1);
  const [efficiency, setEfficiency] = useState(null);

  // The whole score response, kept verbatim so the admin JSON viewer can show exactly what the card was built from.
  const [scoreData, setScoreData] = useState(null);
  const requestInFlightRef = useRef(false);
  const [calculated_output_dimensions, set_calculated_output_dimensions] = useState("")

  const [cuttingMachineLayoutDimensionsInMmWidth, setCuttingMachineLayoutDimensionsInMmWidth] = useState(0)
  const [cuttingMachineLayoutDimensionsInMmHeight, setCuttingMachineLayoutDimensionsInMmHeight] = useState(0)
  const [printingMachineLayoutDimensionInMmWidth, setPrintingMachineLayoutDimensionInMmWidth] = useState(0)
  const [printingMachineLayoutDimensionInMmHeight, setPrintingMachineLayoutDimensionInMmHeight] = useState(0)

  const [cutting_machine_found, set_cutting_machine_found] = useState(false);
  const [printing_machine_found, set_printing_machine_found] = useState(false);
  const [dieCuttingMachineIdentifier, setDieCuttingMachineIdentifier] = useState(null);
  const [printingMachineLabel, setPrintingMachineLabel] = useState(null);
  const [dieCuttingMachineLabel, setDieCuttingMachineLabel] = useState(null);
  const [printingMachineTypeLabel, setPrintingMachineTypeLabel] = useState(null);
  const [dieCuttingMachineTypeLabel, setDieCuttingMachineTypeLabel] = useState(null);

  // Apply score data to component state
  const applyScoreData = (data) => {
    setScoreData(() => data);
    setScore(() => data.score);
    setNumTotal(() => data.diecutting_num_total);
    set_cutting_machine_found(() => data.cutting_machine_found);
    set_printing_machine_found(() => data.printing_machine_found);
    setNumOfDrawingInSet(() => data.num_of_drawings_in_set);
    setPrintingImageUrl(() => data.printing_result_image_url);
    setDiecuttingImageUrl(() => data.diecutting_result_image_url);
    setEfficiency(() => data.efficiency);

    setCuttingScore(() => data._score_debug.cutting);
    setCuttingMachineScore(() => data._score_debug.cutting_machine);
    setCuttingMetalStripScore(() => data._score_debug.cutting_metal_strip);
    setCuttingBoardScore(() => data._score_debug.cutting_board);
    setCuttingBoardFemaleScore(() => data._score_debug.cutting_board_female);
    setPrintingScore(() => data._score_debug.printing);
    setFlexoPrintingAndDieCuttingScore(() => data._score_debug.flexo_printing_and_die_cutting ?? null);
    setMaterialScore(() => data._score_debug.material);
    setGuillotineScore(() => data._score_debug.guillotine ?? null);
    set_kesim_num_total(() => data._score_debug.kesim_num_total);
    set_print_num_total(() => data._score_debug.print_num_total);

    setCuttingMachineLayoutDimensionsInMmWidth(() => data.cutting_machine_layout_dimensions_in_mm?.width);
    setCuttingMachineLayoutDimensionsInMmHeight(() => data.cutting_machine_layout_dimensions_in_mm?.height);

    setPrintingMachineLayoutDimensionInMmWidth(() => data.printing_machine_layout_dimension_in_mm?.width);
    setPrintingMachineLayoutDimensionInMmHeight(() => data.printing_machine_layout_dimension_in_mm?.height);

    setDieCuttingMachineIdentifier(() => data.die_cutting_machine_identifier);
    setPrintingMachineLabel(() => data.printing_machine_label);
    setDieCuttingMachineLabel(() => data.die_cutting_machine_label);
    setPrintingMachineTypeLabel(() => data.printing_machine_type_label);
    setDieCuttingMachineTypeLabel(() => data.die_cutting_machine_type_label);

    set_calculated_output_dimensions(() => {
      const dimensions = data.calculated_output_dimensions;
      if (!dimensions?.output_x_length || !dimensions?.output_y_length) {
        return "";
      }
      const xLength = parseFloat(dimensions.output_x_length);
      const yLength = parseFloat(dimensions.output_y_length);
      if (isNaN(xLength) || isNaN(yLength)) {
        return "";
      }
      return xLength.toFixed(2) + " x " + yLength.toFixed(2);
    });

    setNumTotals((prev) => {
      return {
        ...prev,
        [index]: data.diecutting_num_total,
      };
    });

    setNumTotalsPrinting((prev) => {
      return {
        ...prev,
        [index]: data.printing_num_total,
      };
    });

    setNumOfDrawingsInSet((prev) => {
      return {
        ...prev,
        [index]: data.num_of_drawings_in_set,
      };
    });
  };

  const getScore = () => {
    if (score > 0) { return; }
    if (requestInFlightRef.current) { return; }

    const uuid = diecutting_result.uuid;

    // Check cache first - prevents duplicate API calls for same solution
    if (hasInCache(uuid)) {
      const cachedData = getFromCache(uuid);
      applyScoreData(cachedData);
      return;
    }

    requestInFlightRef.current = true;

    QueuedPostRequest({
      url: `/layouts-nav-section-combination-score`,
      data: {
        formula_options: window.dielineOptions,
        diecutting_result: diecutting_result,
        nav_section_id: nav_section_id,
        count: count,
        id: window.drawingId,
        layout_dimension_id: layout_dimension.id
      },
      callback: (data) => {
        // Cache the response for reuse
        setInCache(uuid, data);
        applyScoreData(data);
        requestInFlightRef.current = false;
      }
    }).catch((error) => {
      console.error('[LayoutNavSectionCombination getScore] Unhandled error:', error);
      requestInFlightRef.current = false;
    });
  }

  useEffect(() => {
    setTotalScoreList(prev => ({
      ...prev,
      [index]: score,
    }));
  }, [score]);

  const ENTER_EDGE = '#eb5424';
  const toDisplayMm = (valueMm) => (
    window.user_unit == 'in'
      ? `${formatDimension((parseFloat(valueMm) / 25.4).toFixed(2))}in`
      : `${formatDimension(valueMm)}mm`
  );
  // A labeled rectangle: width along the bottom edge, height up one side. The height label sits on the left by
  // default; pass heightLabelSide: 'right' to put it on the right. The die-cutting column uses the right side so
  // the two columns' height labels land on the card's outer edges and the narrow space between the boxes stays
  // clear (the left-side label was getting cramped onto the box there).
  const labeledRect = ({ widthMm, heightMm, widthPx, heightPx, borderColor, background, children, heightLabelSide = 'left' }) => {
    const onRight = heightLabelSide === 'right';
    const heightLabel = (
      <div style={{ fontSize: '11px', color: '#777', lineHeight: 1, writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'center', height: `${heightPx}px`, [onRight ? 'marginLeft' : 'marginRight']: '3px' }}>{toDisplayMm(heightMm)}</div>
    );
    const box = (
      <div style={{
        position: 'relative', width: `${widthPx}px`, height: `${heightPx}px`, boxSizing: 'border-box',
        border: `2px solid ${borderColor}`, borderRadius: '2px', background,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {children}
        <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '3px', fontSize: '11px', color: '#777', lineHeight: 1, whiteSpace: 'nowrap' }}>{toDisplayMm(widthMm)}</div>
      </div>
    );
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {onRight ? box : heightLabel}
        {onRight ? heightLabel : box}
      </div>
    );
  };

  // Flow figures: take each column's raw solution image and rotate it 90deg ONLY when needed so the long side
  // of its printed/cut area (its layout_dimension) is horizontal — i.e. rotate when that layout_dimension is
  // portrait (taller than wide); if it's already landscape, leave it as-is. Everything is drawn on one shared
  // scale so real proportions are honored (a 1000x700 sheet is bigger than a 700x500). Die-cut green, print blue.
  const flowMaterial = layout_dimension || { width: 0, height: 0 };
  // A WITHOUT-FOLDING solution (carton_lamination, corrugated/flexo) has no fixed material sheet: the finder
  // hands the draw an unconstrained marker board instead of a real one (LayoutDimension::UNCONSTRAINED_SHEET_MM,
  // installed by SolutionFinderWithoutFolding#large_dummy_dimension), and the REAL sheet moves to
  // diecutting_result.layout_dimension. That marker is not a size — scaling the figure to it flattens every
  // rectangle to ~0px (PR #5230) — so it must never be treated as material dimensions.
  //
  // Asked as `solution_without_folding`, the flag the SAME finder sets on the solution, rather than by comparing
  // the width against a copy of the marker's size: the two are set together server-side, so the flag cannot drift
  // out of step with the board, whereas a duplicated literal silently could.
  const isUnconstrainedSheet = Boolean(diecutting_result.solution_without_folding);
  const materialWidthMm = isUnconstrainedSheet ? 0 : (parseFloat(flowMaterial.width) || 0);
  const materialHeightMm = isUnconstrainedSheet ? 0 : (parseFloat(flowMaterial.height) || 0);
  // The material sheet's name + size (e.g. "B2 500mm X 707mm"), shown at the top of the card the way the
  // selected ManufacturingLine card names it. A card must always name the board it sits on, which matters most
  // now that ONE manufacturing line offers a card per found-for board. Through the shared helper, because the
  // priced view names the same board from the same solution (SelectedSolutionSections) and the two must agree.
  const materialSheetName = materialSheetLabel(diecutting_result, layout_dimension);
  // false for a die-cutting-only solution (hard cardboard), whose payload carries no printing_result at all —
  // it draws no printing column, so this rotation flag is unused there and must not read a missing sheet.
  const printingRotate = diecutting_result.printing_result
    ? parseFloat(diecutting_result.printing_result.layout_dimension.height) > parseFloat(diecutting_result.printing_result.layout_dimension.width)
    : false;
  const dieCuttingRotate = parseFloat(diecutting_result.layout_dimension.height) > parseFloat(diecutting_result.layout_dimension.width);
  // The solution SVG pads its sheet by 10 units on every side (viewBox "-10 -10 w+20 h+20"), so its drawn sheet
  // is only width/(width+20) of the image. Size the image to the padded sheet and clip, so the visible sheet is
  // exactly its real size at flowScale — matching the padding-free machine rectangle so the two are to scale.
  const SVG_PADDING_MM = 10;
  // The material image is rendered server-side at a fixed orientation (the solution rotates it by its own flag).
  // Its viewBox is the source of truth for the material's real proportions, so we size/rotate the displayed sheet
  // to IT — not to layout_dimension, which can come back transposed and make the image float inside its box.
  const svgViewBoxDimensions = (dataUri) => {
    try {
      const base64 = (dataUri || '').split(',')[1] || '';
      const match = atob(base64.slice(0, 400)).match(/viewBox="-?[\d.]+ -?[\d.]+ ([\d.]+) ([\d.]+)"/);
      if (match) { return { width: parseFloat(match[1]), height: parseFloat(match[2]) }; }
    } catch (error) { /* test stub or malformed data URI — the caller falls back to the layout dimensions */ }
    return null;
  };
  // One column's drawn material sheet in mm: the image's viewBox minus its padding, falling back to the
  // material layout_dimension when the SVG can't be read (e.g. the test stub).
  const drawnMaterialDimensionsMm = (svgUrl) => {
    const viewBox = svgViewBoxDimensions(svgUrl);
    if (viewBox) {
      return { width: viewBox.width - 2 * SVG_PADDING_MM, height: viewBox.height - 2 * SVG_PADDING_MM };
    }
    return { width: materialWidthMm, height: materialHeightMm };
  };
  // The shared scale covers exactly what is drawn: each column's material sheet (viewBox-derived — NOT the raw
  // layout_dimension, which can be the unconstrained-sheet marker above) and both machine beds.
  const printingMaterialDimensionsMm = drawnMaterialDimensionsMm(printingImageUrl);
  const dieCuttingMaterialDimensionsMm = drawnMaterialDimensionsMm(diecuttingImageUrl);
  const flowMaximumMm = Math.max(
    printingMaterialDimensionsMm.width, printingMaterialDimensionsMm.height,
    dieCuttingMaterialDimensionsMm.width, dieCuttingMaterialDimensionsMm.height,
    parseFloat(printingMachineLayoutDimensionInMmWidth) || 0, parseFloat(printingMachineLayoutDimensionInMmHeight) || 0,
    parseFloat(cuttingMachineLayoutDimensionsInMmWidth) || 0, parseFloat(cuttingMachineLayoutDimensionsInMmHeight) || 0
  );
  // Largest drawn dimension in px. Kept modest so the two columns (image + its vertical height label) fit a
  // quarter-width card without crowding; the die-cutting column's label sits on the right (see labeledRect).
  const FLOW_TARGET_PX = 130;
  const flowScale = flowMaximumMm > 0 ? FLOW_TARGET_PX / flowMaximumMm : 0;

  // A full feed arrow: a shaft + a triangle head. The material feeds into the machine, the orange edge first.
  const feedArrow = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '2px', height: '20px', background: ENTER_EDGE }} />
      <div style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `9px solid ${ENTER_EDGE}` }} />
    </div>
  );

  // Compute one column's flow pieces — the material image, the machine bed, and the "material in machine" overlay
  // — so the card can lay the two columns out row by row and bottom-align each row. (Each column used to be its
  // own independent vertical stack, which left the rows out of line whenever the two columns' images differed in
  // height.) Rotation/scale logic is unchanged: rotate 90deg only when the image's own viewBox is portrait.
  const computeFlow = ({ machineW, machineH, rotate, svgUrl, kind }) => {
    const machineWidthMm = parseFloat(machineW) || 0;
    const machineHeightMm = parseFloat(machineH) || 0;
    const hasMachine = Boolean(flowScale) && machineWidthMm > 0 && machineHeightMm > 0;
    const color = MACHINE_KIND[kind].color;
    const tint = MACHINE_BED_TINT[kind];
    // Printing (left column) keeps its height label on the left; die-cutting (right column) puts it on the right,
    // so both labels sit on the card's outer edges and the gap between the two boxes stays clear.
    const heightLabelSide = kind === 'die_cutting' ? 'right' : 'left';
    // Material proportions + rotation come from the image's own viewBox (the source of truth), falling back to
    // the layout dimensions + the passed rotate flag when the SVG can't be read (e.g. the test stub).
    const viewBox = svgViewBoxDimensions(svgUrl);
    const { width: materialW, height: materialH } = drawnMaterialDimensionsMm(svgUrl);
    const displayRotate = viewBox ? materialH > materialW : rotate;
    const hasMaterial = Boolean(flowScale) && materialW > 0 && materialH > 0;
    const displayedWidthMm = displayRotate ? materialH : materialW;
    const displayedHeightMm = displayRotate ? materialW : materialH;
    const paddedImageWidthPx = (materialW + 2 * SVG_PADDING_MM) * flowScale;
    const paddedImageHeightPx = (materialH + 2 * SVG_PADDING_MM) * flowScale;
    // The raw solution image, rotated 90deg only when its printed/cut area's long side is vertical. The count text
    // is counter-rotated inside the SVG (count_text_rotate, server-side) so it lands upright either way.
    const imageElement = () => (
      <img src={svgUrl} style={{ width: `${paddedImageWidthPx}px`, height: `${paddedImageHeightPx}px`, flexShrink: 0, ...(displayRotate ? { transform: 'rotate(90deg)' } : {}) }} />
    );

    const materialImage = hasMaterial ? labeledRect({
      widthMm: displayedWidthMm, heightMm: displayedHeightMm,
      widthPx: displayedWidthMm * flowScale, heightPx: displayedHeightMm * flowScale,
      borderColor: 'transparent', background: 'transparent',
      heightLabelSide: heightLabelSide,
      children: imageElement()
    }) : null;

    // The machine bed shows the same faint icon (print / scissors) as the ManufacturingLine cards.
    const machineBoxWidthPx = machineWidthMm * flowScale;
    const machineBoxHeightPx = machineHeightMm * flowScale;
    const machineBed = hasMachine ? labeledRect({
      widthMm: machineWidthMm, heightMm: machineHeightMm,
      widthPx: machineBoxWidthPx, heightPx: machineBoxHeightPx,
      borderColor: color, background: tint,
      heightLabelSide: heightLabelSide,
      children: <MachineIcon kind={kind} widthPx={machineBoxWidthPx} heightPx={machineBoxHeightPx} />
    }) : null;

    // The same material image laid over the machine bed, horizontally centered and sharing the bed's bottom edge.
    // A sheet taller than the bed overflows above it; its printable bottom rests on the bed.
    let materialInMachine = null;
    if (hasMaterial && hasMachine) {
      const materialBoxWidthPx = displayedWidthMm * flowScale;
      const materialBoxHeightPx = displayedHeightMm * flowScale;
      materialInMachine = (
        <div style={{ position: 'relative', width: `${Math.max(machineBoxWidthPx, materialBoxWidthPx)}px`, height: `${Math.max(machineBoxHeightPx, materialBoxHeightPx)}px` }}>
          <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: `${machineBoxWidthPx}px`, height: `${machineBoxHeightPx}px`, boxSizing: 'border-box', border: `2px solid ${color}`, borderRadius: '2px', background: tint }} />
          <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: `${materialBoxWidthPx}px`, height: `${materialBoxHeightPx}px`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {imageElement()}
          </div>
        </div>
      );
    }

    return { hasMaterial, hasMachine, materialImage, machineBed, materialInMachine };
  };


  // WAS THIS SOLUTION RUN BY ONE MACHINE? A COMBINED (flexo) manufacturing line prints AND die-cuts in a single
  // pass, and the score is where that fact arrives: a combined figure having been taken IS the one-pass shape
  // (Frank::DieCuttingSolution#combined_pass?) -- the same rule the breakdown panel below switches on, asked of
  // the same value, so the figure and the price cannot describe different plans.
  const isCombinedPass = flexoPrintingAndDieCuttingScore != null;

  // WAS THIS SOLUTION RUN WITHOUT A PRESS AT ALL? Read exactly like the combined pass above, off what was
  // SCORED: a part_type that is never printed (hard cardboard) is made on a die-cutting machine alone, so
  // Frank::SolutionScore takes no printing pass and Frank::DieCuttingSolution#score_debug carries no `printing`
  // key at all. Its absence is therefore the fact that nothing printed this solution -- which is why that key is
  // omitted rather than sent as zero, since a zero would read as a press priced at nothing.
  const isDieCuttingOnlyPass = !isCombinedPass && printingScore == null;

  // THE PASSES THIS SOLUTION IS DRAWN AS, one column each: a press and a die-cutter make two, a combined
  // machine makes ONE.
  //
  // ONE MACHINE, ONE COLUMN. Drawn as two, a combined manufacturing line repeated the same machine, the same
  // bed and the same sheet side by side under "Baskı" and "Bıçak" -- a card that reads as a two-machine plan,
  // which is a different plan from the one-pass machine the viewer picked on the ManufacturingLine card.
  // The single column is headed the way that card heads it (ManufacturingLineCard#machineColumns).
  //
  // NOTHING IS LOST BY COLLAPSING THEM. A combined machine is offered only to the flexo part_types
  // (Frank::SubSectionManufacturingLines::MachineTypeFilters#rows), which solve WITHOUT folding, and that
  // strategy gangs nothing -- PrintingSolutionFinder answers a single 1x1 printing solution, so the sheet that
  // is printed IS the sheet that is cut (Frank::SolutionFinderWithoutFolding). The two columns were one sheet,
  // through one machine, drawn twice.
  const flowColumns = () => {
    if (isCombinedPass) {
      return [{
        key: 'combined',
        label: `${t('layouts.layout_type_die_cutting')} + ${t('layouts.layout_type_printing')}`,
        machineLabel: printingMachineLabel,
        machineTypeLabel: printingMachineTypeLabel,
        machinePriced: printing_machine_found && cutting_machine_found,
        flow: computeFlow({
          // The combined machine's own bed, read through the PRINTING keys -- the pair the ManufacturingLine
          // card's combined column reads too. For a combined line these are one bed: Frank::ManufacturingLine
          // answers the combined machine's sheet for both #sheet_*_mm and #printing_sheet_*_mm.
          machineW: printingMachineLayoutDimensionInMmWidth, machineH: printingMachineLayoutDimensionInMmHeight,
          // The DIE-CUTTING picture: the sheet with its copies drawn as they are cut, which is the printed
          // sheet as well (see above).
          rotate: dieCuttingRotate, svgUrl: diecuttingImageUrl, kind: 'combined'
        })
      }];
    }

    // NO PRESS, ONE COLUMN -- the same collapse the combined machine gets above, for the opposite reason: there
    // is no printing machine to head a second column with, so drawing one would read as a plan whose press is
    // missing rather than a plan with no press in it.
    if (isDieCuttingOnlyPass) {
      return [{
        key: 'die_cutting',
        label: t('layouts.layout_type_die_cutting'),
        machineLabel: dieCuttingMachineLabel,
        machineTypeLabel: dieCuttingMachineTypeLabel,
        machinePriced: cutting_machine_found,
        flow: computeFlow({ machineW: cuttingMachineLayoutDimensionsInMmWidth, machineH: cuttingMachineLayoutDimensionsInMmHeight, rotate: dieCuttingRotate, svgUrl: diecuttingImageUrl, kind: 'die_cutting' })
      }];
    }

    return [
      {
        key: 'printing',
        label: t('layouts.layout_type_printing'),
        machineLabel: printingMachineLabel,
        machineTypeLabel: printingMachineTypeLabel,
        machinePriced: printing_machine_found,
        flow: computeFlow({ machineW: printingMachineLayoutDimensionInMmWidth, machineH: printingMachineLayoutDimensionInMmHeight, rotate: printingRotate, svgUrl: printingImageUrl, kind: 'printing' })
      },
      {
        key: 'die_cutting',
        label: t('layouts.layout_type_die_cutting'),
        machineLabel: dieCuttingMachineLabel,
        machineTypeLabel: dieCuttingMachineTypeLabel,
        machinePriced: cutting_machine_found,
        flow: computeFlow({ machineW: cuttingMachineLayoutDimensionsInMmWidth, machineH: cuttingMachineLayoutDimensionsInMmHeight, rotate: dieCuttingRotate, svgUrl: diecuttingImageUrl, kind: 'die_cutting' })
      }
    ];
  };

  const card = () => {
    return <div>
      {/* Real production order: print first (gangs N-up), then split and die-cut. The columns are laid out
          row by row — labels, material image, feed arrow, machine bed, material-in-machine — in one grid so that
          within each row the columns' images share a baseline: align-items:end pins every cell to the row's
          bottom and the grid sizes each row to its taller image. */}
      {(() => {
        const columns = flowColumns();
        const anyMachine = columns.some((column) => column.flow.hasMachine);
        const anyMaterialInMachine = columns.some((column) => Boolean(column.flow.materialInMachine));
        // As many equal columns as there are passes: two for a press + die-cutter, one for a combined machine.
        const gridColumns = {
          display: 'grid',
          gridTemplateColumns: columns.map(() => 'minmax(0, 1fr)').join(' '),
          columnGap: '12px',
          justifyItems: 'center'
        };
        // Each grid cell is wrapped so a column missing a piece (e.g. an unpriced machine) still holds its slot,
        // keeping the columns aligned; align-items:end on the grid bottom-aligns the cells in every row.
        const cell = (content) => <div style={{ display: 'flex', justifyContent: 'center' }}>{content}</div>;
        const materialInMachineLabel = () => <div style={{ fontSize: '10px', color: '#999', textAlign: 'center' }}>material in machine</div>;
        // The machine bed with the machine's name written just above it (the name the ManufacturingLine
        // cards use), and under the name its type ("Digital cutting", "Offset", ...) — so the card says
        // WHAT kind of machine runs the pass, for example the digital cutter a whole-sheet L run needs.
        const machineWithName = (label, typeLabel, bed) => bed ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            {label && <div style={{ fontSize: '12px', fontWeight: 600, color: '#222', lineHeight: 1.2, textAlign: 'center' }}>{label}</div>}
            {typeLabel && <div style={{ fontSize: '11px', fontWeight: 400, color: '#888', lineHeight: 1.2, textAlign: 'center', marginTop: '-2px' }}>{typeLabel}</div>}
            {bed}
          </div>
        ) : null;
        // The "material in machine" label sits just above its overlay in the same cell, so the gap between them is
        // small; the data grid's larger rowGap then only applies above the whole section.
        const materialInMachineCell = (overlay) => overlay ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            {materialInMachineLabel()}
            {overlay}
          </div>
        ) : null;
        return (
          <>
          {/* Column headers in their own row so the gap down to the first rectangles is small. The data grid below
              uses a larger rowGap to clear the width labels that hang under each rectangle — which headers lack. */}
          <div style={{ ...gridColumns, marginTop: '16px' }}>
            {columns.map((column) => (
              <div key={column.key} className={styles.layout_column_label}>{column.label}</div>
            ))}
          </div>
          <div style={{ ...gridColumns, rowGap: '22px', alignItems: 'end' }}>
            {columns.map((column) => (
              <React.Fragment key={`material:${column.key}`}>{cell(column.flow.materialImage)}</React.Fragment>
            ))}

            {/* Every arrow cell renders together (keeps the columns paired), but each shows its arrow only when
                that column actually has a machine below it — no arrow pointing into an empty cell. */}
            {anyMachine && columns.map((column) => (
              <React.Fragment key={`arrow:${column.key}`}>{cell(column.flow.hasMachine ? feedArrow() : null)}</React.Fragment>
            ))}

            {/* Pull the machine name up close to the arrow. The grid rowGap is sized for the width labels that hang
                under the rectangles; the arrow → name pair has no such label, so it gets a negative top margin.
                align-items:end still pins the bed to the row baseline, so the beds stay bottom-aligned. */}
            {columns.map((column) => (
              <div key={`machine:${column.key}`} style={{ display: 'flex', justifyContent: 'center', marginTop: '-12px' }}>
                {machineWithName(column.machineLabel, column.machineTypeLabel, column.flow.machineBed)}
              </div>
            ))}

            {anyMaterialInMachine && <hr style={{ gridColumn: '1 / -1', width: '100%', border: 'none', borderTop: '1px solid #eee', margin: 0 }} />}
            {anyMaterialInMachine && columns.map((column) => (
              <React.Fragment key={`material-in-machine:${column.key}`}>{cell(materialInMachineCell(column.flow.materialInMachine))}</React.Fragment>
            ))}
          </div>

          {/* Per-column footnotes below the figure: the die-cutting machine identifier and "machine price not
              found". A combined machine is one column, so it says either once rather than twice. */}
          <div style={{ ...gridColumns, marginTop: '8px' }}>
            {columns.map((column) => (
              <div key={`footnote:${column.key}`}>
                {column.key !== 'printing' && dieCuttingMachineIdentifier && (
                  <span className={styles.efficiency_badge}>{dieCuttingMachineIdentifier}</span>
                )}
                {!column.machinePriced && <strong> Makine birim fiyat bulunamadi</strong>}
              </div>
            ))}
          </div>
          </>
        );
      })()}

      {window.isAdminShowDebug && (
        <div style={debugPanelStyle}>
          <span style={debugLabelStyle}>Skor</span>
          <span style={debugValueStyle}>{formatDebugNumber(score)}</span>

          <span style={debugSeparatorStyle} />

          {/* A COMBINED (flexo) machine is ONE billed pass, so it gets one row named for what it is -- there is
              no Baskı figure and no Kesim figure to show beside it. Every other solution is two machines and
              keeps the two-row breakdown. */}
          {flexoPrintingAndDieCuttingScore != null ? (
            <>
              <span style={debugLabelStyle}>Baskı & Kesim</span>
              <span style={debugValueStyle}>{formatDebugNumber(flexoPrintingAndDieCuttingScore)}</span>
            </>
          ) : (
            <>
              {/* No Baskı row when nothing printed this solution: the breakdown says what was billed, and a
                  press that is not in the plan was not. */}
              {!isDieCuttingOnlyPass && (
                <>
                  <span style={debugLabelStyle}>Baskı</span>
                  <span style={debugValueStyle}>{formatDebugNumber(printingScore)}</span>
                </>
              )}

              <span style={debugLabelStyle}>Kesim</span>
              <span style={debugValueStyle}>{formatDebugNumber(cuttingScore)}</span>

              {cuttingMachineScore != null && (
                <>
                  <span style={debugSubLabelStyle}>Kesim makine</span>
                  <span style={debugValueStyle}>{formatDebugNumber(cuttingMachineScore)}</span>
                </>
              )}
              {cuttingMetalStripScore != null && (
                <>
                  <span style={debugSubLabelStyle}>Kesim metal şerit</span>
                  <span style={debugValueStyle}>{formatDebugNumber(cuttingMetalStripScore)}</span>
                </>
              )}
              {cuttingBoardScore != null && (
                <>
                  <span style={debugSubLabelStyle}>Kesim tahtası</span>
                  <span style={debugValueStyle}>{formatDebugNumber(cuttingBoardScore)}</span>
                </>
              )}
              {cuttingBoardFemaleScore != null && (
                <>
                  <span style={debugSubLabelStyle}>Ayıklama tahtası (dişi)</span>
                  <span style={debugValueStyle}>{formatDebugNumber(cuttingBoardFemaleScore)}</span>
                </>
              )}
            </>
          )}

          <span style={debugLabelStyle}>Malzeme</span>
          <span style={debugValueStyle}>{formatDebugNumber(materialScore)}</span>

          {guillotineScore != null && (
            <>
              <span style={debugLabelStyle}>Giyotin</span>
              <span style={debugValueStyle}>{formatDebugNumber(guillotineScore)}</span>
            </>
          )}

          <span style={debugSeparatorStyle} />

          {/* No printing count for a die-cutting-only solution (hard cardboard): it has no printing pass, so the
              row shows the cut count alone rather than a phantom "baskı" figure beside it. */}
          <span style={debugLabelStyle}>{isDieCuttingOnlyPass ? 'Toplam adet (kesim)' : 'Toplam adet (baskı / kesim)'}</span>
          <span style={debugValueStyle}>{isDieCuttingOnlyPass ? kesim_num_total : `${print_num_total} / ${kesim_num_total}`}</span>

          <span style={debugLabelStyle}>Çıktı boyutu</span>
          <span style={debugValueStyle}>{calculated_output_dimensions ? `${calculated_output_dimensions} mm` : '—'}</span>

          {dieCuttingMachineIdentifier && (
            <>
              <span style={debugLabelStyle}>Makine</span>
              <span style={debugValueStyle}>{dieCuttingMachineIdentifier}</span>
            </>
          )}
        </div>
      )}
    </div>
  }

  useEffect( getScore, [ diecutting_result, count] )

  if(score == 0) { return <div key={name} data-score={score}> loading....</div> }

  const hasMissingMachine = !cutting_machine_found || !printing_machine_found;

  // The data behind this card, assembled for the admin JSON viewer. The base64 SVG data URIs are stripped —
  // they are not meaningful as JSON and would bury the dimensions/options the viewer is meant to surface.
  const withoutImageDataUris = (response) => {
    if (!response) {
      return response;
    }
    const placeholder = (value) => (
      typeof value === 'string' && value.startsWith('data:')
        ? `[svg data uri, ${value.length} chars — omitted]`
        : value
    );
    return {
      ...response,
      diecutting_result_image_url: placeholder(response.diecutting_result_image_url),
      printing_result_image_url: placeholder(response.printing_result_image_url),
    };
  };

  const solutionJson = {
    efficiency: efficiency,
    // The sheet the parts are laid out on. The prop is named layout_dimension, but it is the material sheet —
    // distinct from the part's own layout_dimension nested under diecutting_result below.
    material_layout_dimension: layout_dimension,
    // The die-cutting result exactly as the server sent it: the part's layout_dimension, rotate, num_x/num_y,
    // the nested printing_result, margins and machine_dimensions.
    diecutting_result: diecutting_result,
    // Everything the scoring endpoint returned for this solution.
    score_response: withoutImageDataUris(scoreData),
  };

  return (
    <div className={`${styles.combinations_grid_item} ${hasMissingMachine ? 'missing-machine' : ''}`}
      key={name}
      data-score={score}
      data-num-total={numTotal}
      data-num-of-drawing-in-set={numOfDrawingInSet}>

      {efficiency !== null && (
        <span className={styles.efficiency_badge_top_right}>
          <HoverTooltip title={t('quote_builder.efficiency_tooltip', { efficiency: `${efficiency.toFixed(2)}%` })}>
            {efficiency.toFixed(2)}%
          </HoverTooltip>
        </span>
      )}

      {/* Badge the solution with its sub_section label -- the SAME label the Manufacturing Line (plan) card
          shows for this group -- so the two agree. Fall back to the template's LayoutPart names only when a
          section_label is absent (a drawing whose templates carry no LayoutTab). */}
      {diecutting_result.member.section_label ? (
        <div className={styles.part_badges_container} style={{ justifyContent: 'center', marginTop: efficiency !== null ? '12px' : 0 }}>
          <span className={styles.efficiency_badge}>{diecutting_result.member.section_label}</span>
        </div>
      ) : (diecutting_result.member.part_names && diecutting_result.member.part_names.length > 0 && (
        <div className={styles.part_badges_container} style={{ justifyContent: 'center' }}>
          {diecutting_result.member.part_names.map((partName, idx) => (
            <span key={idx} className={styles.efficiency_badge}>{partName.trim()}</span>
          ))}
        </div>
      ))}

      {/* The material sheet these parts are laid out on (e.g. "B2 500mm X 707mm"), at the top of the card
          the way the selected ManufacturingLine card names it -- so a solution read in isolation says which
          sheet it is for. */}
      {materialSheetName && (
        <div style={{ textAlign: 'center', fontSize: '13px', marginTop: '8px' }}>
          <span style={{ color: '#888' }}>{t('quote_builder.material_dimensions')}:</span>{' '}
          <strong style={{ color: '#333' }}>{materialSheetName}</strong>
        </div>
      )}

      {card()}

      {/* Admin-only "View JSON" lives at the very bottom of the card so it sits below the flow figures. */}
      {window.isAdminShowDebug && scoreData && (
        <SolutionJsonViewer data={solutionJson} />
      )}

    </div>
  )
}

export default LayoutNavSectionCombination;
